import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { formatBytes } from "../../../utils";
import {
  Box,
  Button,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  useTheme,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import { FileConfig, FileDetails } from "../../../types/process";
import { DEFAULT_FILE_CONFIG } from "../../../constants/process";

interface FileInputProps {
  id: string;
  label?: string;
  config?: Partial<FileConfig>;
  currentFiles?: FileDetails[];
  onChange: (files: File[], fieldName: string) => void;
  onRemoveExisting?: (fileId: string, fieldName: string) => void;
  fieldName: string;
  required?: boolean;
  containerClassName?: string;
}

const FileInput: React.FC<FileInputProps> = ({
  id,
  label,
  config = {},
  currentFiles = [],
  onChange,
  onRemoveExisting,
  fieldName,
  required,
  containerClassName,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const effectiveConfig: FileConfig = { ...DEFAULT_FILE_CONFIG, ...config };
  const {
    maxFiles = 5,
    maxSizeMB = 5,
    allowedTypes = DEFAULT_FILE_CONFIG.allowedTypes,
  } = effectiveConfig;
  const maxSizeInBytes = maxSizeMB * 1024 * 1024;

  const [selectedNewFiles, setSelectedNewFiles] = useState<File[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const allDisplayableFilesCount =
    currentFiles.length + selectedNewFiles.length;

  const handleFileValidation = (file: File): string | null => {
    if (file.size > maxSizeInBytes) {
      return t("file_too_large", { fileName: file.name, maxSize: maxSizeMB });
    }
    if (
      allowedTypes &&
      !allowedTypes.includes(file.type) &&
      !allowedTypes.some(
        (type) =>
          type.endsWith("/*") && file.type.startsWith(type.slice(0, -1)),
      )
    ) {
      return t("invalid_file_type", {
        fileName: file.name,
        allowedTypes: allowedTypes.join(", "),
      });
    }
    return null;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    processFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const processFiles = (files: File[]) => {
    const newErrors: string[] = [];
    const newValidFiles: File[] = [];

    if (allDisplayableFilesCount + files.length > maxFiles) {
      newErrors.push(
        t("max_files_exceeded", {
          fileCount: files.length,
          maxFiles,
          currentCount: allDisplayableFilesCount,
        }),
      );
    } else {
      files.forEach((file) => {
        const error = handleFileValidation(file);
        if (error) {
          newErrors.push(error);
        } else {
          newValidFiles.push(file);
        }
      });
    }

    setFileErrors(newErrors);
    if (newValidFiles.length > 0) {
      const updatedFiles = [...selectedNewFiles, ...newValidFiles];
      setSelectedNewFiles(updatedFiles);
      onChange(updatedFiles, fieldName);
    } else if (newErrors.length === 0 && files.length > 0) {
      onChange(selectedNewFiles, fieldName);
    }
  };

  const removeNewFile = (index: number) => {
    const updatedNewFiles = selectedNewFiles.filter((_, i) => i !== index);
    setSelectedNewFiles(updatedNewFiles);
    onChange(updatedNewFiles, fieldName);
    setFileErrors([]);
  };

  const handleRemoveExistingFile = (fileId: string) => {
    if (onRemoveExisting) {
      onRemoveExisting(fileId, fieldName);
      setFileErrors([]);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
    processFiles(files);
  };

  return (
    <Box className={containerClassName} sx={{ mb: 2 }}>
      {label && (
        <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
          {label}{" "}
          {required && (
            <span style={{ color: theme.palette.error.main }}>*</span>
          )}
        </Typography>
      )}

      <Paper
        sx={{
          p: 3,
          border: 2,
          borderStyle: "dashed",
          borderColor: isDragging ? "primary.main" : "divider",
          backgroundColor: isDragging ? "action.hover" : "background.paper",
          transition: "all 0.2s",
          cursor:
            allDisplayableFilesCount >= maxFiles ? "not-allowed" : "pointer",
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <Box sx={{ textAlign: "center" }}>
          <CloudUploadIcon
            sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
          />
          <Typography variant="body2" sx={{ mb: 1 }}>
            <Button
              component="label"
              variant="text"
              disabled={allDisplayableFilesCount >= maxFiles}
              sx={{ textTransform: "none" }}
            >
              {t("upload_files")}
              <input
                ref={fileInputRef}
                id={id}
                name={id}
                type="file"
                multiple={maxFiles > 1}
                hidden
                onChange={handleFileChange}
                accept={allowedTypes?.join(",")}
                disabled={allDisplayableFilesCount >= maxFiles}
              />
            </Button>
            {t("or_drag_drop")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {allowedTypes?.map((t) => t.split("/")[1].toUpperCase()).join(", ")}{" "}
            {t("up_to")} {maxSizeMB}MB {t("each")}.{t("max")} {maxFiles}{" "}
            {t("files")}.
          </Typography>
        </Box>
      </Paper>

      {allDisplayableFilesCount >= maxFiles && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t("maximum_files_reached", { maxFiles })}
        </Alert>
      )}

      {fileErrors.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {fileErrors.map((error, index) => (
            <Alert key={index} severity="error" sx={{ mb: 0.5 }}>
              {error}
            </Alert>
          ))}
        </Box>
      )}

      {(currentFiles.length > 0 || selectedNewFiles.length > 0) && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("selected_files")} ({allDisplayableFilesCount}/{maxFiles}):
          </Typography>
          <List dense>
            {currentFiles.map((file) => (
              <ListItem
                key={file.id}
                sx={{ backgroundColor: "grey.50", mb: 0.5, borderRadius: 1 }}
              >
                <ListItemIcon>
                  <InsertDriveFileIcon />
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={formatBytes(file.size)}
                />
                {onRemoveExisting && (
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      color="error"
                      onClick={() => handleRemoveExistingFile(file.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
            {selectedNewFiles.map((file, index) => (
              <ListItem
                key={file.name + index}
                sx={{ backgroundColor: "grey.50", mb: 0.5, borderRadius: 1 }}
              >
                <ListItemIcon>
                  <AttachFileIcon />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {file.name}
                      <Typography
                        variant="caption"
                        sx={{ color: "info.main", fontStyle: "italic" }}
                      >
                        {t("new")}
                      </Typography>
                    </Box>
                  }
                  secondary={formatBytes(file.size)}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    color="error"
                    onClick={() => removeNewFile(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default FileInput;
